/**
 * Schemas for ninas + ninas_critico tabs (per data-model V1.1 §3.2 + §3.3).
 *
 * PII tier framework:
 * - ninas: tier Alto (nombre, fecha_nacimiento, folio_gdm, tutor_*) + Medio (rama, estatus)
 * - ninas_critico: tier Crítico (notas_medicas, alergias, ficha_medica)
 *
 * RTBF híbrido por tier (privacy.md §4):
 * - Crítico → hard-delete
 * - Alto → pseudonimización SHA-256+sal
 * - Medio → tombstone
 */
import { z } from 'zod';
import { UlidSchema, VersionedFieldsSchema, listResponseSchema } from './common.js';

// ─── Estatus enum ──────────────────────────────────────────────────────────

export const EstatusNinaSchema = z.enum(['activa', 'baja', 'pausa', 'egresada']);
export type EstatusNina = z.infer<typeof EstatusNinaSchema>;

// ─── ninas tab (master, tier Alto/Medio) ───────────────────────────────────

export const NinaSchema = VersionedFieldsSchema.extend({
  // Tier Alto fields
  nombre_completo: z.string().min(2),
  fecha_nacimiento: z.string().date(),
  folio_gdm: z.string().min(1),
  tutor_nombre: z.string().min(2),
  tutor_email: z.string().email(),
  tutor_telefono: z.string().min(7), // E.164 minimum

  // Tier Medio fields
  rama_actual: z.string().min(1),
  estatus: EstatusNinaSchema,
  fecha_alta: z.string().date(),
  fecha_baja: z.string().date().nullable(),
  motivo_baja: z.string().nullable(),

  // Forward-compat V2 (per RISK-001 migration plan)
  consent_signed_at: z.string().date().nullable(),
  consent_url: z.string().url().nullable(),

  // V1.1 patch (per FWA-SEC-014 G1 R3-1+R3-9)
  acuse_inscripcion_url: z.string().url().nullable(),
  acuse_inscripcion_signed_at: z.string().date().nullable(),
  acuse_inscripcion_version: z.string().nullable(),
  nina_alias_id: z.string(), // SHA-256+sal hash, server-computed

  // Tombstone (RTBF tier Medio)
  deleted: z.boolean().default(false),
  deleted_at: z.string().datetime().nullable(),
});

export type Nina = z.infer<typeof NinaSchema>;

/**
 * Create payload (POST /v1/ninas).
 * Excludes server-computed fields (entity_id, version_id, etc.) + derivados (nina_alias_id, quarter).
 */
export const NinaCreatePayloadSchema = z.object({
  nombre_completo: NinaSchema.shape.nombre_completo,
  fecha_nacimiento: NinaSchema.shape.fecha_nacimiento,
  folio_gdm: NinaSchema.shape.folio_gdm,
  tutor_nombre: NinaSchema.shape.tutor_nombre,
  tutor_email: NinaSchema.shape.tutor_email,
  tutor_telefono: NinaSchema.shape.tutor_telefono,
  rama_actual: NinaSchema.shape.rama_actual,
  fecha_alta: NinaSchema.shape.fecha_alta,
  acuse_inscripcion_url: NinaSchema.shape.acuse_inscripcion_url.optional(),
  acuse_inscripcion_signed_at: NinaSchema.shape.acuse_inscripcion_signed_at.optional(),
  acuse_inscripcion_version: NinaSchema.shape.acuse_inscripcion_version.optional(),
});

export type NinaCreatePayload = z.infer<typeof NinaCreatePayloadSchema>;

/**
 * Update payload (PATCH /v1/ninas/:id).
 * All fields optional; If-Match header enforces version.
 */
export const NinaUpdatePayloadSchema = NinaCreatePayloadSchema.partial();
export type NinaUpdatePayload = z.infer<typeof NinaUpdatePayloadSchema>;

// ─── ninas_critico tab (tier Crítico aislado) ──────────────────────────────

export const NinaCriticoSchema = VersionedFieldsSchema.extend({
  // entity_id is FK to ninas.entity_id (same value, 1-to-1)
  notas_medicas: z.string().max(2000).nullable(),
  alergias: z.string().max(500).nullable(),
  ficha_medica_url: z.string().url().nullable(),
  ficha_medica_drive_file_id: z.string().nullable(), // V1.1 patch
  ficha_medica_filename: z.string().nullable(),
});

export type NinaCritico = z.infer<typeof NinaCriticoSchema>;

export const NinaCriticoCreatePayloadSchema = z.object({
  notas_medicas: NinaCriticoSchema.shape.notas_medicas.optional(),
  alergias: NinaCriticoSchema.shape.alergias.optional(),
  ficha_medica_url: NinaCriticoSchema.shape.ficha_medica_url.optional(),
  ficha_medica_filename: NinaCriticoSchema.shape.ficha_medica_filename.optional(),
});

export type NinaCriticoCreatePayload = z.infer<typeof NinaCriticoCreatePayloadSchema>;

export const NinaCriticoUpdatePayloadSchema = NinaCriticoCreatePayloadSchema.partial();
export type NinaCriticoUpdatePayload = z.infer<typeof NinaCriticoUpdatePayloadSchema>;

// ─── Cross-rama handover (per spec §6.13) ──────────────────────────────────

export const NinaRamaChangePayloadSchema = z.object({
  to_rama: z.string().min(1),
  effective_date: z.string().date(),
});

export type NinaRamaChangePayload = z.infer<typeof NinaRamaChangePayloadSchema>;

export const NinaRamaChangeResponseSchema = z.object({
  nina_entity_id: UlidSchema,
  from_rama: z.string(),
  to_rama: z.string(),
  effective_date: z.string().date(),
  jefa_anterior_overlap_window_days: z.number().int().default(30),
  notif_tutor_sent: z.boolean(),
});

export type NinaRamaChangeResponse = z.infer<typeof NinaRamaChangeResponseSchema>;

// ─── List responses ────────────────────────────────────────────────────────

export const NinaListResponseSchema = listResponseSchema(NinaSchema);
export type NinaListResponse = z.infer<typeof NinaListResponseSchema>;
